import React from 'react'

import useCurrency from '@/hooks/use-currency'
import Currency from '@/types/currency'

import type {SetError} from '@/hooks/use-form-errors'

export const initialState = {
    invoiceNumber: '',
    invoiceIssueDate: undefined as undefined | Date,
    invoiceDueDate: undefined as undefined | Date,
    yourCompanyLogo: '',
    yourCompanyName: '',
    yourEmail: '',
    yourAddress: '',
    yourCity: '',
    yourZipCode: '',
    yourState: '',
    yourCountry: '',
    recipientsCompanyName: '',
    recipientsEmail: '',
    recipientsAddress: '',
    recipientsCity: '',
    recipientsZipCode: '',
    recipientsState: '',
    recipientsCountry: '',
    invoiceItems: [{id: '1', name: '', price: 0, quantity: 0}],
    currency: Currency.EUR,
    notes: '',
    terms: '',
    tax: 0,
    subtotal: 0,
}

export type State = typeof initialState
export type StateKey = keyof State

export enum Actions {
    SET_VALUES = 'SET_VALUE',
    INVOICE_ITEM_ADDED = 'INVOICE_ITEM_ADDED',
    INVOICE_ITEM_DELETED = 'INVOICE_ITEM_DELETED',
    INVOICE_ITEM_UPDATED = 'INVOICE_ITEM_UPDATED',
    RESET_FORM = 'RESET_FORM',
}

type SetValue = {
    type: Actions
    id?: string
    key?: any
    value?: any
}

type ActionType = SetValue

export type Dispatch = React.Dispatch<ActionType>

export const reducer = (state: State, action: ActionType) => {
    switch (action.type) {
        case Actions.SET_VALUES: {
            return {
                ...state,
                [action.key]: action.value,
            }
        }

        case Actions.INVOICE_ITEM_ADDED: {
            if (!action.id) {
                return state
            }

            return {
                ...state,
                invoiceItems: state.invoiceItems.concat({
                    id: action.id,
                    name: '',
                    price: 0,
                    quantity: 0,
                }),
            }
        }

        case Actions.INVOICE_ITEM_DELETED: {
            if (!action.id) {
                return state
            }

            return {
                ...state,
                invoiceItems: state.invoiceItems.filter((item) => item.id !== action.id),
            }
        }

        case Actions.INVOICE_ITEM_UPDATED: {
            if (!action.id) {
                return state
            }

            return {
                ...state,
                invoiceItems: updateItemInPlace(state.invoiceItems, action.id, {
                    [action.key]: action.value,
                }),
            }
        }

        case Actions.RESET_FORM: {
            return initialState
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const updateItemInPlace = (invoiceItems: State['invoiceItems'], id: string, value: any) =>
    invoiceItems.map((item) => (item.id === id ? {...item, ...value} : item))

export const useInvoiceGenerator = () => {
    const [state, dispatch] = React.useReducer(reducer, initialState)
    const currency = useCurrency()

    React.useEffect(() => {
        dispatch({
            type: Actions.SET_VALUES,
            key: 'currency',
            value: currency.code,
        })
    }, [currency.code, dispatch])

    const isFormValid = (setError?: SetError<State>) => {
        const requiredFields: StateKey[] = [
            'invoiceNumber',
            'invoiceIssueDate',
            'invoiceDueDate',
            'yourCompanyName',
            'yourEmail',
            'yourAddress',
            'yourCity',
            'yourZipCode',
            'yourState',
            'yourCountry',
            'recipientsCompanyName',
            'recipientsEmail',
            'recipientsAddress',
            'recipientsCity',
            'recipientsZipCode',
            'recipientsState',
            'recipientsCountry',
            'tax',
        ]

        let isValid = true

        requiredFields.forEach((field) => {
            if (!state[field]) {
                setError?.(field, 'This field is required')
                isValid = false
            }
        })

        state.invoiceItems.forEach((item) => {
            if (!item.name) {
                setError?.(`item-name-${item.id}` as any, 'This field is required')
                isValid = false
            }
        })

        return isValid
    }

    return {state, dispatch, isFormValid}
}
